import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, Mail } from "lucide-react";
import { CONTACT, INSTRUMENT_TYPES, VIDEOS } from "@/data/content";
import VideoBackdrop from "@/components/VideoBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAILTO = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Calibration quote request")}`;

const EMPTY = { name: "", company: "", phone: "", email: "", instrument_type: "", message: "" };
const FIELD_CLS =
  "mt-2 rounded-none border-line/20 bg-transparent text-ink placeholder:text-dim focus-visible:ring-0 focus-visible:border-brand transition-colors duration-200";

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!form.email.trim()) er.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email";
    if (!form.instrument_type) er.instrument_type = "Please choose an instrument type";
    if (!form.message.trim()) er.message = "Please tell us what you need calibrated";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      console.error("Quote submission failed", err);
      setStatus("error");
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative overflow-hidden border-y border-line/10 bg-base2 py-20 md:py-24">
      <VideoBackdrop
        sources={VIDEOS.riverLight.sources}
        poster={VIDEOS.riverLight.poster}
        alt="Sunlit river water flowing slowly"
        rate={0.4}
        className="theme-video opacity-[0.16]"
        testId="quote-video-backdrop"
      />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-b from-base2 via-base2/60 to-base2" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(800px 420px at 85% 20%, rgb(var(--c-brand)/0.07), transparent 65%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-5">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-2"
        >
          <p className="hud-label">[ 04 / QUOTE ]</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold uppercase tracking-tight text-ink md:text-4xl lg:text-5xl">
            Tell us what you need calibrated
          </h2>
          <p className="mt-6 text-base leading-relaxed text-body md:text-lg">
            Instrument type, make and model, and whether lab or on-site suits you. We confirm scope and come back with a quote — no obligation.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
          className="lg:col-span-3"
        >
          {status === "success" ? (
            <div
              data-testid="quote-success-message"
              role="status"
              className="corner-brackets glass flex h-full flex-col items-start justify-center border border-brand/40 p-10"
            >
              <CheckCircle2 className="h-12 w-12 text-brand" aria-hidden="true" />
              <h3 className="mt-5 font-heading text-2xl font-semibold tracking-tight text-ink">Quote request received</h3>
              <p className="mt-3 text-base leading-relaxed text-body-strong">
                Thanks — we have your details and will be in touch to confirm scope and provide your quote.
              </p>
              <p className="mt-4 text-sm text-body">
                Nothing arrived?{" "}
                <a href={MAILTO} data-testid="quote-success-mailto" className="font-semibold text-brand hover:underline">
                  Email us directly
                </a>
                .
              </p>
              <Button
                variant="outline"
                data-testid="quote-send-another-button"
                onClick={() => setStatus("idle")}
                className="mt-8 rounded-full border-brand/40 bg-transparent text-brand hover:border-brand hover:bg-brand/10 hover:text-brand-hi"
              >
                Send another request
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="quote-form"
              className="corner-brackets glass border border-line/10 p-7 sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="quote-name" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Name *</Label>
                  <Input
                    id="quote-name"
                    data-testid="quote-name-input"
                    aria-label="Your name"
                    value={form.name}
                    onChange={set("name")}
                    className={FIELD_CLS}
                    placeholder="Your name"
                  />
                  {errors.name && <p data-testid="quote-name-error" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="quote-company" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Company</Label>
                  <Input
                    id="quote-company"
                    data-testid="quote-company-input"
                    aria-label="Company"
                    value={form.company}
                    onChange={set("company")}
                    className={FIELD_CLS}
                    placeholder="Company or site"
                  />
                </div>
                <div>
                  <Label htmlFor="quote-phone" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Phone</Label>
                  <Input
                    id="quote-phone"
                    type="tel"
                    data-testid="quote-phone-input"
                    aria-label="Phone number"
                    value={form.phone}
                    onChange={set("phone")}
                    className={FIELD_CLS}
                    placeholder="Best contact number"
                  />
                </div>
                <div>
                  <Label htmlFor="quote-email" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Email *</Label>
                  <Input
                    id="quote-email"
                    type="email"
                    data-testid="quote-email-input"
                    aria-label="Email address"
                    value={form.email}
                    onChange={set("email")}
                    className={FIELD_CLS}
                    placeholder="you@company.co.nz"
                  />
                  {errors.email && <p data-testid="quote-email-error" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="quote-instrument" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Instrument type *</Label>
                  <Select value={form.instrument_type} onValueChange={set("instrument_type")}>
                    <SelectTrigger
                      id="quote-instrument"
                      data-testid="quote-instrument-select"
                      aria-label="Instrument type"
                      className="mt-2 rounded-none border-line/20 bg-transparent text-ink focus:ring-0 focus:border-brand data-[placeholder]:text-dim"
                    >
                      <SelectValue placeholder="Choose an instrument type" />
                    </SelectTrigger>
                    <SelectContent className="border-brand/25 bg-surface text-ink">
                      {INSTRUMENT_TYPES.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          data-testid={`quote-instrument-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="focus:bg-brand/15 focus:text-ink"
                        >
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.instrument_type && <p data-testid="quote-instrument-error" className="mt-1.5 text-xs text-red-400">{errors.instrument_type}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="quote-message" className="font-mono text-xs uppercase tracking-[0.18em] text-body-strong">Message *</Label>
                  <Textarea
                    id="quote-message"
                    data-testid="quote-message-input"
                    aria-label="Message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className={FIELD_CLS}
                    placeholder="Make, model, range, quantity, and whether you prefer lab or on-site calibration"
                  />
                  {errors.message && <p data-testid="quote-message-error" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                </div>
              </div>

              {status === "error" && (
                <div
                  data-testid="quote-error-message"
                  role="alert"
                  className="mt-6 flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>
                    Something went wrong sending your request. Please{" "}
                    <a href={MAILTO} data-testid="quote-error-mailto" className="font-semibold underline">
                      email us directly
                    </a>
                    .
                  </span>
                </div>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-5">
                <Button
                  type="submit"
                  size="lg"
                  disabled={status === "sending"}
                  data-testid="quote-submit-button"
                  className="rounded-full bg-brand px-9 font-semibold text-base shadow-[0_0_20px_rgb(var(--c-brand)/0.35)] transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-hi hover:shadow-[0_0_32px_rgb(var(--c-brand)/0.6)] disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Send quote request"}
                </Button>
                <a
                  href={MAILTO}
                  data-testid="quote-mailto-fallback"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-body transition-colors duration-200 hover:text-brand"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  or email us instead
                </a>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
